import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
    ticketsListWrapper: {
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    },

    ticketsList: {
        flex: 1,
        maxHeight: "100%",
        overflowY: "scroll",
        ...theme.scrollbarStyles,
        borderTop: "2px solid rgba(0, 0, 0, 0.12)",
    },

    ticketsListHeader: {
        color: "rgb(67, 83, 105)",
        zIndex: 2,
        backgroundColor: "white",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },

    ticketsCount: {
        fontWeight: "normal",
        color: "rgb(104, 121, 146)",
        marginLeft: "8px",
        fontSize: "14px",
    },

    noTicketsText: {
        textAlign: "center",
        color: "rgb(104, 121, 146)",
        fontSize: "14px",
        lineHeight: "1.4",
    },

    noTicketsTitle: {
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "600",
        margin: "0px",
    },

    noTicketsDiv: {
        display: "flex",
        // height: "190px",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
}));

const ticketSortAsc = (a, b) => {
    
    if (a.updatedAt < b.updatedAt) {
        return -1;
    }
    if (a.updatedAt > b.updatedAt) {
        return 1;
    }
    return 0;
}

const ticketSortDesc = (a, b) => {
   
    if (a.updatedAt > b.updatedAt) {
        return -1;
    }
    if (a.updatedAt < b.updatedAt) {
        return 1;
    }
    return 0;
}

const shouldClearHotFields = (ticket) => {
    const nonHotKanbanStages = [
        "Novo Lead",
        "Em qualificação",
        "Qualificado",
        "Em nutrição",
        "Convertido",
        "Perdido",
        "Pós-Venda",
        "Retenção"
    ];

    const ticketTagNames = (ticket?.tags || [])
        .map(tag => tag?.name)
        .filter(Boolean);

    return ticketTagNames.some(name => nonHotKanbanStages.includes(name));
}

const clearHotFields = (ticket) => ({
    ...ticket,
    isHot: false,
    hotState: null,
    hotEventId: null,
    leadScore: ticket?.leadScore,
    leadTemperature: ticket?.leadTemperature
});

const preserveHotFields = (currentTicket, incomingTicket) => {
    if (!currentTicket) return incomingTicket;

    if (shouldClearHotFields(incomingTicket)) {
        return clearHotFields(incomingTicket);
    }

    if (incomingTicket?.isHot === undefined && currentTicket?.isHot) {
        return {
            ...incomingTicket,
            isHot: currentTicket.isHot,
            hotState: currentTicket.hotState,
            hotEventId: currentTicket.hotEventId,
            leadScore: currentTicket.leadScore,
            leadTemperature: currentTicket.leadTemperature
        };
    }

    return incomingTicket;
}

const reducer = (state, action) => {
    //console.log("action", action, state)
    const sortDir = action.sortDir;
    
    if (action.type === "LOAD_TICKETS") {
        const newTickets = action.payload || [];
        const baseState = action.pageNumber === 1 ? [] : state;

        newTickets.forEach((ticket) => {
            const ticketIndex = baseState.findIndex((t) => t.id === ticket.id);
            if (ticketIndex !== -1) {
                baseState[ticketIndex] = preserveHotFields(baseState[ticketIndex], ticket);
                if (ticket.unreadMessages > 0) {
                    baseState.unshift(baseState.splice(ticketIndex, 1)[0]);
                }
            } else {
                baseState.push(ticket);
            }
        });
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? baseState.sort(ticketSortAsc) : baseState.sort(ticketSortDesc);
        }

        return [...baseState];
    }

    if (action.type === "RESET_UNREAD") {
        const ticketId = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state[ticketIndex].unreadMessages = 0;
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            const previousTicket = state[ticketIndex];

            const mergedTicket = {
                ...previousTicket,
                ...ticket,
                isHot: ticket.isHot !== undefined ? ticket.isHot : previousTicket.isHot,
                hotState: ticket.hotState !== undefined ? ticket.hotState : previousTicket.hotState,
                hotEventId: ticket.hotEventId !== undefined ? ticket.hotEventId : previousTicket.hotEventId,
                leadScore: ticket.leadScore !== undefined ? ticket.leadScore : previousTicket.leadScore,
                leadTemperature: ticket.leadTemperature !== undefined ? ticket.leadTemperature : previousTicket.leadTemperature
            };

            state[ticketIndex] = shouldClearHotFields(ticket)
                ? clearHotFields(mergedTicket)
                : mergedTicket;
        } else {
            state.unshift(ticket);
        }
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            state[ticketIndex] = preserveHotFields(state[ticketIndex], ticket);
            state.unshift(state.splice(ticketIndex, 1)[0]);
        } else {
            if (action.status === action.payload.status) {
                state.unshift(ticket);
            }
        }
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_CONTACT") {
        const contact = action.payload;
        const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
        if (ticketIndex !== -1) {
            state[ticketIndex].contact = contact;
        }
        return [...state];
    }

    if (action.type === "DELETE_TICKET") {
        const ticketId = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state.splice(ticketIndex, 1);
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const TicketsListCustom = (props) => {
    const {
        setTabOpen,
        status,
        searchParam,
        searchOnMessages,
        tags,
        users,
        showAll,
        selectedQueueIds,
        updateCount,
        style,
        whatsappIds,
        forceSearch,
        statusFilter,
        userFilter,
        sortTickets
    } = props;

    const classes = useStyles();
    const [pageNumber, setPageNumber] = useState(1);
    const [realtimeRefreshTick, setRealtimeRefreshTick] = useState(0);
    let [ticketsList, dispatch] = useReducer(reducer, []);
    //   const socketManager = useContext(SocketContext);
    const { user, socket } = useContext(AuthContext);

    const { profile, queues } = user;
    const showTicketWithoutQueue = user.allTicket === 'enable' && selectedQueueIds.includes("__WITHOUT_QUEUE__");
    const companyId = user.companyId;

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [status, searchParam, dispatch, showAll, tags, users, forceSearch, realtimeRefreshTick, selectedQueueIds, whatsappIds, statusFilter, sortTickets, searchOnMessages]);

    const showWithoutQueue = selectedQueueIds.includes("__WITHOUT_QUEUE__");
    const selectedQueueIdsForBackend = selectedQueueIds.filter(id => id !== "__WITHOUT_QUEUE__");

    const { tickets, hasMore, loading, count } = useTickets({
        pageNumber,
        searchParam,
        status,
        showAll,
        searchOnMessages: searchOnMessages ? "true" : "false",
        tags: JSON.stringify(tags),
        users: JSON.stringify(users),
        queueIds: JSON.stringify(selectedQueueIdsForBackend),
        whatsappIds: JSON.stringify(whatsappIds),
        statusFilter: JSON.stringify(statusFilter),
        userFilter,
        sortTickets,
        forceSearch: `${forceSearch || ""}-${realtimeRefreshTick}`,
        showWithoutQueue: showWithoutQueue ? "true" : "false"
    });

    const isTicketAllowedByQueueFilter = (ticket) => {
        if (!Array.isArray(selectedQueueIds) || selectedQueueIds.length === 0) return false;

        const hasAll = selectedQueueIds.includes("__ALL__");
        const hasWithoutQueue = selectedQueueIds.includes("__WITHOUT_QUEUE__");

        const realQueueIds = selectedQueueIds
            .filter(id => id !== "__ALL__" && id !== "__WITHOUT_QUEUE__")
            .map(id => Number(id));

        if (hasAll) return true;
        if (!ticket?.queueId) return hasWithoutQueue;

        return realQueueIds.includes(Number(ticket.queueId));
    };



    useEffect(() => {
        // const queueIds = queues.map((q) => q.id);
        // const filteredTickets = tickets.filter(
        //     (t) => queueIds.indexOf(t.queueId) > -1
        // );
        // const allticket = user.allTicket === 'enabled';
        // if (profile === "admin" || allTicket || allowGroup || allHistoric) {
        if (companyId) {
            dispatch({
                type: "LOAD_TICKETS",
                payload: tickets,
                status,
                sortDir: sortTickets,
                pageNumber
            });
        }
        // } else {
        //  dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
        // }

    }, [tickets, pageNumber]);

    useEffect(() => {
        const shouldUpdateTicket = ticket => {
            return (!ticket?.userId || ticket?.userId === user?.id || showAll) &&
                isTicketAllowedByQueueFilter(ticket)
            // (!blockNonDefaultConnections || (ticket.status == 'group' && ignoreUserConnectionForGroups) || !user?.whatsappId || ticket.whatsappId == user?.whatsappId);
        }
        // const shouldUpdateTicketUser = (ticket) =>
        //     selectedQueueIds.indexOf(ticket?.queueId) > -1 && (ticket?.userId === user?.id || !ticket?.userId);

        const notBelongsToUserQueues = (ticket) =>
            ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

        const refreshTicketsFromApi = () => {
            setPageNumber(1);
            setRealtimeRefreshTick(prev => prev + 1);
        };

        const onCompanyTicketTicketsList = (data) => {
            console.log("========== V270 SOCKET DIAGNOSTICO ==========");
            console.log("EVENT:", `company-${companyId}-ticket`);
            console.log("ABA_STATUS_LOCAL:", status);
            console.log("ACTION:", data?.action);
            console.log("TICKET_ID:", data?.ticket?.id || data?.ticketId);
            console.log("TICKET_UUID:", data?.ticket?.uuid);
            console.log("TICKET_STATUS:", data?.ticket?.status);
            console.log("TICKET_QUEUE_ID:", data?.ticket?.queueId);
            console.log("TICKET_USER_ID:", data?.ticket?.userId);
            console.log("TICKET_LAST_MESSAGE:", data?.ticket?.lastMessage);
            console.log("PAYLOAD_COMPLETO:", data);
            console.log("============================================");
            console.log("=== SOCKET UPDATE TICKETSLIST DEBUG ===");
            console.log("ACTION:", data?.action);
            console.log("STATUS_ATUAL_ABA:", status);
            console.log("STATUS_RECEBIDO:", data?.ticket?.status);
            console.log("QUEUE_RECEBIDA:", data?.ticket?.queueId);
            console.log("USER_RECEBIDO:", data?.ticket?.userId);
            console.log("ISHOT:", data?.ticket?.isHot);
            console.log("TICKET_RECEBIDO:", data?.ticket);
            console.log("======================================");
            // console.log("onCompanyTicketTicketsList", data)
            if (data.action === "updateUnread") {
                dispatch({
                    type: "RESET_UNREAD",
                    payload: data.ticketId,
                    status: status,
                    sortDir: sortTickets
                });
            }
            // console.log(shouldUpdateTicket(data.ticket))
            if (data.action === "update") {
                const ticketBelongsToCurrentStatus = data.ticket?.status === status || data.ticket?.isHot;
                const ticketBelongsToCurrentFilter = shouldUpdateTicket(data.ticket);

                if (ticketBelongsToCurrentStatus && ticketBelongsToCurrentFilter) {
                    dispatch({
                        type: "UPDATE_TICKET",
                        payload: data.ticket,
                        status: status,
                        sortDir: sortTickets
                    });
                } else {
                    dispatch({
                        type: "DELETE_TICKET",
                        payload: data.ticket?.id,
                        status: status,
                        sortDir: sortTickets
                    });
                }
            }

            // else if (data.action === "update" && shouldUpdateTicketUser(data.ticket) && data.ticket.status === status) {
            //     dispatch({
            //         type: "UPDATE_TICKET",
            //         payload: data.ticket,
            //     });
            // }
            if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                    sortDir: sortTickets
                });
            }

            if (data.action === "delete") {
                dispatch({
                    type: "DELETE_TICKET", payload: data?.ticketId, status: status,
                    sortDir: sortTickets
                });
                refreshTicketsFromApi();

            }

            if (data.action === "update") {
                refreshTicketsFromApi();
            }
        };

        const onCompanyAppMessageTicketsList = (data) => {
            if (data.action === "create" &&
                shouldUpdateTicket(data.ticket) && data.ticket.status === status) {
                dispatch({
                    type: "UPDATE_TICKET_UNREAD_MESSAGES",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
            }
            // else if (data.action === "create" && shouldUpdateTicketUser(data.ticket) && data.ticket.status === status) {
            //     dispatch({
            //         type: "UPDATE_TICKET_UNREAD_MESSAGES",
            //         payload: data.ticket,
            //     });
            // }
        };

        const onCompanyContactTicketsList = (data) => {
            if (data.action === "update" && data.contact) {
                dispatch({
                    type: "UPDATE_TICKET_CONTACT",
                    payload: data.contact,
                    status: status,
                    sortDir: sortTickets
                });
            }
        };

        const onConnectTicketsList = () => {
            if (status) {
                socket.emit("joinTickets", status);
            } else {
                socket.emit("joinNotification");
            }
        }

        socket.on("connect", onConnectTicketsList)
        socket.on(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
        socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
        socket.on(`company-${companyId}-contact`, onCompanyContactTicketsList);

        return () => {
            if (status) {
                socket.emit("leaveTickets", status);
            } else {
                socket.emit("leaveNotification");
            }
            socket.off("connect", onConnectTicketsList);
            socket.off(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
            socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
            socket.off(`company-${companyId}-contact`, onCompanyContactTicketsList);
        };

    }, [status, showAll, user, selectedQueueIds, tags, users, profile, queues, sortTickets, showTicketWithoutQueue]);

    useEffect(() => {
        if (typeof updateCount === "function") {
            updateCount(count || 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count]);

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;

        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    if (status && status !== "search") {
        ticketsList = ticketsList.filter(ticket => ticket.status === status)
    }

    return (
        <Paper className={classes.ticketsListWrapper} style={style}>
            <Paper
                square
                name="closed"
                elevation={0}
                className={classes.ticketsList}
                onScroll={handleScroll}
            >
                <List style={{ paddingTop: 0 }} >
                    {ticketsList.length === 0 && !loading ? (
                        <div className={classes.noTicketsDiv}>
                            <span className={classes.noTicketsTitle}>
                                {i18n.t("ticketsList.noTicketsTitle")}
                            </span>
                            <p className={classes.noTicketsText}>
                                {i18n.t("ticketsList.noTicketsMessage")}
                            </p>
                        </div>
                    ) : (
                        <>
                            {ticketsList.filter(isTicketAllowedByQueueFilter).map((ticket) => (
                                // <List key={ticket.id}>
                                //     {console.log(ticket)}
                                <TicketListItem
                                    ticket={ticket}
                                    key={ticket.id}
                                    setTabOpen={setTabOpen}
                                />
                                // </List>
                            ))}
                        </>
                    )}
                    {loading && <TicketsListSkeleton />}
                </List>
            </Paper>
        </Paper>
    );
};

export default TicketsListCustom;
